import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Bell, Plus, Trash2, Calendar, Sparkles, Clock, CheckCircle, BellRing, BellOff, Wifi, WifiOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  requestNotificationPermission, 
  getNotificationPermission, 
  scheduleReminderNotification,
  isOnline,
  onNetworkStatusChange
} from '@/utils/notifications';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REMINDER_TYPES = [
  {
    id: 'new_reading',
    name: 'Nouvelle lecture',
    description: 'Rappel pour faire une nouvelle analyse',
    icon: Sparkles,
    defaultMessage: "C'est le moment de faire une nouvelle lecture de main pour suivre votre évolution !"
  },
  {
    id: 'check_evolution',
    name: 'Vérifier évolution',
    description: 'Comparer avec vos anciennes lectures',
    icon: Clock,
    defaultMessage: "Prenez un moment pour comparer vos lectures et observer votre évolution."
  },
  {
    id: 'custom',
    name: 'Personnalisé',
    description: 'Créez votre propre rappel',
    icon: Bell,
    defaultMessage: ""
  }
];

const RemindersPage = ({ sessionId }) => {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [customMessage, setCustomMessage] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(getNotificationPermission());
  const [online, setOnline] = useState(isOnline());

  useEffect(() => {
    fetchReminders();
    
    // Listen for network status changes
    const cleanup = onNetworkStatusChange((status) => {
      setOnline(status);
      if (status) {
        toast.success('Connexion rétablie');
        fetchReminders();
      } else {
        toast.warning('Mode hors-ligne activé');
      }
    });

    return cleanup;
  }, [sessionId]);

  const fetchReminders = async () => {
    if (!sessionId) return;

    try {
      const response = await axios.get(`${API}/reminders/${sessionId}`, { withCredentials: true });
      setReminders(response.data);
      
      // Cache reminders for offline access
      localStorage.setItem('cachedReminders', JSON.stringify(response.data));
    } catch (error) {
      console.error('Error fetching reminders:', error);
      
      // Try to load from cache if offline
      if (!isOnline()) {
        const cached = localStorage.getItem('cachedReminders');
        if (cached) {
          setReminders(JSON.parse(cached));
          toast.info('Affichage des rappels en cache');
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const enableNotifications = async () => {
    const granted = await requestNotificationPermission();
    setNotificationPermission(granted ? 'granted' : 'denied');
    
    if (granted) {
      toast.success('Notifications activées ! Vous recevrez vos rappels.');
    } else {
      toast.error('Notifications refusées. Activez-les dans les paramètres de votre navigateur.');
    }
  };

  const createReminder = async () => {
    if (!selectedType || !selectedDate) {
      toast.error('Veuillez sélectionner un type et une date');
      return;
    }

    setIsCreating(true);

    try {
      const typeInfo = REMINDER_TYPES.find(t => t.id === selectedType);
      const message = selectedType === 'custom' ? customMessage : typeInfo?.defaultMessage;

      const response = await axios.post(`${API}/reminders/create`, {
        session_id: sessionId,
        reminder_type: selectedType,
        message: message,
        scheduled_date: new Date(selectedDate).toISOString()
      }, { withCredentials: true });

      // Schedule local notification if permissions granted
      if (notificationPermission === 'granted' && response.data) {
        scheduleReminderNotification({
          id: response.data.id,
          message: message,
          scheduled_date: selectedDate
        });
        toast.success('Rappel créé avec notification programmée !');
      } else {
        toast.success('Rappel créé !');
      }
      
      setShowCreateModal(false);
      setSelectedType(null);
      setCustomMessage('');
      setSelectedDate('');
      fetchReminders();
    } catch (error) {
      console.error('Error creating reminder:', error);
      toast.error('Erreur lors de la création du rappel');
    } finally {
      setIsCreating(false);
    }
  };

  const deleteReminder = async (reminderId) => {
    try {
      await axios.delete(`${API}/reminders/${reminderId}?session_id=${sessionId}`, { withCredentials: true });
      setReminders(reminders.filter(r => r.id !== reminderId));
      toast.success('Rappel supprimé');
    } catch (error) {
      console.error('Error deleting reminder:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const isDatePassed = (dateString) => {
    return new Date(dateString) < new Date();
  };

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().slice(0, 16);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 py-12 px-4 md:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Offline Banner */}
        {!online && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3"
          >
            <WifiOff className="h-5 w-5 text-amber-600" />
            <span className="text-sm text-amber-800">
              Mode hors-ligne - Les données affichées peuvent ne pas être à jour
            </span>
          </motion.div>
        )}

        {/* Notification Permission Banner */}
        {notificationPermission !== 'granted' && notificationPermission !== 'unsupported' && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-xl flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <BellOff className="h-5 w-5 text-purple-600" />
              <div>
                <p className="text-sm font-medium text-purple-900">Notifications désactivées</p>
                <p className="text-xs text-purple-700">Activez les notifications pour recevoir vos rappels</p>
              </div>
            </div>
            <Button
              onClick={enableNotifications}
              size="sm"
              className="rounded-full bg-purple-600 hover:bg-purple-700 text-white"
            >
              <BellRing className="h-4 w-4 mr-2" />
              Activer
            </Button>
          </motion.div>
        )}

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-6 rounded-full hover:bg-secondary/50"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à l'accueil
          </Button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Bell className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h1 className="font-heading text-3xl md:text-4xl">
                  Mes rappels
                </h1>
                <p className="text-muted-foreground">
                  Ne manquez plus vos lectures
                </p>
              </div>
            </div>
            
            <Button
              data-testid="create-reminder-btn"
              onClick={() => setShowCreateModal(true)}
              className="rounded-full bg-primary hover:bg-primary/90"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nouveau rappel
            </Button>
          </div>
        </motion.div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Sparkles className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : reminders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-purple-50 flex items-center justify-center">
              <Bell className="h-10 w-10 text-purple-400" />
            </div>
            <h2 className="font-heading text-2xl mb-4">Aucun rappel programmé</h2>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              Créez des rappels pour ne jamais oublier de suivre votre évolution
            </p>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="rounded-full bg-primary hover:bg-primary/90 px-8 py-6"
            >
              <Plus className="h-5 w-5 mr-2" />
              Créer mon premier rappel
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {reminders.map((reminder, index) => {
                const typeInfo = REMINDER_TYPES.find(t => t.id === reminder.reminder_type);
                const Icon = typeInfo?.icon || Bell;
                const isPassed = isDatePassed(reminder.scheduled_date);
                
                return (
                  <motion.div
                    key={reminder.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ delay: index * 0.05 }}
                    className={`bg-card p-5 rounded-2xl border shadow-sm transition-all ${
                      isPassed 
                        ? 'border-green-200 bg-green-50/50' 
                        : 'border-border hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isPassed ? 'bg-green-100' : 'bg-purple-100'
                        }`}>
                          {isPassed ? (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          ) : (
                            <Icon className="h-5 w-5 text-purple-600" />
                          )}
                        </div>
                        
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-heading text-base">
                              {typeInfo?.name || 'Rappel'}
                            </h3>
                            {isPassed && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                Passé
                              </span>
                            )}
                          </div>
                          
                          <p className="text-sm text-muted-foreground mb-2">
                            {reminder.message}
                          </p>
                          
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            <span>{formatDate(reminder.scheduled_date)}</span>
                          </div>
                        </div>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteReminder(reminder.id)}
                        className="text-muted-foreground hover:text-destructive rounded-full"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card p-6 rounded-3xl max-w-md w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="font-heading text-2xl mb-6">Nouveau rappel</h2>
              
              {/* Type Selection */}
              <div className="mb-6">
                <label className="text-sm font-medium mb-3 block">Type de rappel</label>
                <div className="space-y-2">
                  {REMINDER_TYPES.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setSelectedType(type.id)}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                        selectedType === type.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <type.icon className={`h-5 w-5 ${
                          selectedType === type.id ? 'text-primary' : 'text-muted-foreground'
                        }`} />
                        <div>
                          <p className="font-medium text-sm">{type.name}</p>
                          <p className="text-xs text-muted-foreground">{type.description}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Custom Message */}
              {selectedType === 'custom' && (
                <div className="mb-6">
                  <label className="text-sm font-medium mb-2 block">Message personnalisé</label>
                  <textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Votre message de rappel..."
                    className="w-full p-3 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none h-24"
                  />
                </div>
              )}
              
              {/* Date Selection */}
              <div className="mb-6">
                <label className="text-sm font-medium mb-2 block">Date et heure</label>
                <input
                  type="datetime-local"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={getMinDate()}
                  className="w-full p-3 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              
              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 rounded-full"
                >
                  Annuler
                </Button>
                <Button
                  onClick={createReminder}
                  disabled={isCreating || !selectedType || !selectedDate}
                  className="flex-1 rounded-full bg-primary hover:bg-primary/90"
                >
                  {isCreating ? 'Création...' : 'Créer'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RemindersPage;
